import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ChatMessage } from "../../../types";
import { QUICK_REACTIONS } from "./chatShared";

interface Props {
  message: ChatMessage;
  anchorBubble: HTMLElement;
  isMine: boolean;
  myReaction: string | undefined;
  onClose: () => void;
  onReply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onReact: (emoji: string | null) => void;
}

// Portals into document.body, matching the original's single reused node
// appended there (ensureActionsPopover()) - .chat-message-actions already
// declares position:absolute in styles.css, this only ever sets top/left.
// Position depends on the popover's own rendered size, so it's measured in
// a useLayoutEffect (after DOM mutation, before paint) rather than computed
// up front - same two-step "render off-screen, then measure and place" the
// original did by rendering hidden first and positioning after.
export function MessageActionsPopover({ message, anchorBubble, isMine, myReaction, onClose, onReply, onEdit, onDelete, onReact }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = anchorBubble.getBoundingClientRect();
    const popRect = el.getBoundingClientRect();

    let top = window.scrollY + rect.top - popRect.height - 8;
    if (top < window.scrollY + 8) top = window.scrollY + rect.bottom + 8;

    let left = window.scrollX + rect.left;
    const maxLeft = window.scrollX + document.documentElement.clientWidth - popRect.width - 8;
    left = Math.max(window.scrollX + 8, Math.min(left, maxLeft));

    setStyle({ top, left });
  }, [anchorBubble, message.id]);

  useLayoutEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div ref={ref} className="chat-message-actions" style={{ top: style.top, left: style.left }}>
      <div className="chat-message-actions-emojis">
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className={`chat-emoji-btn ${emoji === myReaction ? "is-active" : ""}`}
            aria-label={`React with ${emoji}`}
            onClick={() => onReact(emoji === myReaction ? null : emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="chat-message-actions-buttons">
        <button type="button" className="chat-action-btn chat-action-reply" onClick={onReply}>
          <i className="bi bi-reply" aria-hidden="true" /> Reply
        </button>
        {isMine && message.text && (
          <button type="button" className="chat-action-btn chat-action-edit" onClick={onEdit}>
            <i className="bi bi-pencil" aria-hidden="true" /> Edit
          </button>
        )}
        {isMine && (
          <button type="button" className="chat-action-btn chat-action-delete" onClick={onDelete}>
            <i className="bi bi-trash" aria-hidden="true" /> Delete
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
